import { useEffect, useId, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import {
  stationLookupBXService,
  StationLookupOption,
} from 'BoostXpressAdmin/Services/stationLookupBXService'
import { useDebouncedValue } from '../hooks/useStationOperationsList'
import './StationOperationsStationFilter.scss'

interface StationOperationsStationFilterProps {
  value: StationLookupOption | null
  onChange: (station: StationLookupOption | null) => void
}

const stationLabel = (station: StationLookupOption) =>
  [station.businessName, station.branchName].filter(Boolean).join(' : ') || station.id

const StationOperationsStationFilter = ({
  value,
  onChange,
}: StationOperationsStationFilterProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim())
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const {
    data: stations = [],
    isFetching,
    isError,
    refetch,
  } = useQuery(
    ['station-operations', 'station-search', debouncedSearch],
    () => stationLookupBXService.search(debouncedSearch),
    { enabled: isOpen, staleTime: 60_000, retry: false },
  )
  const isSearching = isFetching || search.trim() !== debouncedSearch

  useEffect(() => {
    if (!isOpen) return
    const closeOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', closeOutside)
    return () => document.removeEventListener('mousedown', closeOutside)
  }, [isOpen])

  const choose = (station: StationLookupOption | null) => {
    onChange(station)
    setIsOpen(false)
    setSearch('')
    triggerRef.current?.focus()
  }

  return (
    <div
      className='station-operations-station-filter'
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          setIsOpen(false)
          triggerRef.current?.focus()
        }
      }}
    >
      <button
        type='button'
        ref={triggerRef}
        className='station-operations-station-filter__trigger'
        aria-label={`Filter by station: ${value ? stationLabel(value) : 'All stations'}`}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{value ? stationLabel(value) : 'All stations'}</span>
        <svg width='16' height='16' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
          <path d='m4 6 4 4 4-4' stroke='currentColor' strokeWidth='1.5' />
        </svg>
      </button>
      {isOpen && (
        <div className='station-operations-station-filter__panel' id={panelId}>
          <input
            type='search'
            aria-label='Search stations by name'
            placeholder='Search station name'
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className='station-operations-station-filter__options'>
            <button type='button' aria-pressed={!value} onClick={() => choose(null)}>
              All stations
            </button>
            {isSearching ? (
              <p role='status'>Searching stations...</p>
            ) : isError ? (
              <div role='alert'>
                Could not load stations.
                <button
                  type='button'
                  onClick={() => {
                    void refetch()
                  }}
                >
                  Try again
                </button>
              </div>
            ) : stations.length ? (
              stations.map((station) => (
                <button
                  type='button'
                  key={station.id}
                  aria-pressed={value?.id === station.id}
                  onClick={() => choose(station)}
                >
                  {stationLabel(station)}
                </button>
              ))
            ) : (
              <p>No matching stations.</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default StationOperationsStationFilter
